
import styled from 'styled-components';
import { useState, useEffect } from 'react';
import TileHeaderButton from '../../components/TileHeader/TileHeaderButton';
import ConfirmModal from '../../components/Modal/ConfirmModal';
import { Container, ContentContainer } from '../../components/Wrapper/Wrapper';
import { Link } from 'react-router-dom';
import { getCount, triggerReport } from '../../api/studyListApi';
import usePollReport from '../../hooks/usePollReport';
import { toaster } from '../../helpers/toaster';
// icons
import {
  Microscope,
  HeartPulse,
  Paperclip,
  MapPin,
  ClipboardList,
  Languages,
  Package,
  GitBranch,
  Globe,
  Syringe,
  BookOpen,
  BarChart3,
  Users as UsersIcon,
  ArrowUpRight,
} from 'lucide-react';

import USERTYPES from '../../constants/usertypes';
import { useTenantConfig } from '../../context/TenantConfigContext';

const Dashboard = ({usertype}) => {
  const { config } = useTenantConfig();
  const disabledPages = config.disabledPages || [];
  const [ count, setCount ] = useState({
    studies: 0,
    locations: 0,
    registries: 0,
    attachments: 0,
    global_registries: 0,
    interventions: 0,
    commercialProducts: 0,
    languages: 0,
    developmentStatuses: 0,
    glossary: 0,
  })

  const { startPolling, status: exportStatus } = usePollReport({
    onSuccess: () => { toaster('Export downloaded successfully', 'success'); },
    onError: () => toaster('Failed to generate export. Please try again.', 'error'),
  });

  const [openExportModal, setOpenExportModal] = useState(false);

  const handleExport = () => {
    setOpenExportModal(false);
    triggerReport('fullExport')
      .then(({ task_id }) => {
        toaster('Generating full export...', 'info');
        startPolling(task_id);
      })
      .catch(() => toaster('Failed to start export.', 'error'));
  };

  useEffect(() => {
    getCount().then( res => setCount(res) )
  }, [])

  const toolsCards = [];
  if (!disabledPages.includes('analytics')) {
    toolsCards.push({ to: 'analytics/', icon: BarChart3, title: 'Analytics', count: null });
  }
  if (usertype === USERTYPES.super_admin) {
    toolsCards.push({ to: 'users/', icon: UsersIcon, title: 'Users', count: null });
  }
  if (usertype === USERTYPES.super_admin && (localStorage.getItem('client') === 'public' || localStorage.getItem('client') === 'xogene')) {
    toolsCards.push({ to: 'clients/', icon: null, title: 'Clients', count: null });
  }

  const sections = [
    {
      label: 'Study Content',
      cards: [
        { to: 'studies/', icon: Microscope, title: 'Studies', count: count.studies },
        { to: 'conditions/', icon: HeartPulse, title: 'Conditions', count: count.conditions },
        { to: 'interventions/', icon: Syringe, title: 'Interventions', count: count.interventions },
        { to: 'attachments/', icon: Paperclip, title: 'Attachments', count: count.attachments },
        { to: 'locations/', icon: MapPin, title: 'Locations', count: count.locations },
        { to: 'study-registries/', icon: ClipboardList, title: 'Registries', count: count.registries },
      ],
    },
    {
      label: 'Reference Data',
      cards: [
        { to: 'global-registries/', icon: Globe, title: 'Global Registries', count: count.global_registries },
        { to: 'languages/', icon: Languages, title: 'Languages', count: count.languages },
        { to: 'commercial-products/', icon: Package, title: 'Commercial Products', count: count.commercialProducts, pageKey: 'commercial-products' },
        { to: 'development-status/', icon: GitBranch, title: 'Development Status', count: count.developmentStatuses, pageKey: 'development-status' },
        { to: 'glossary/', icon: BookOpen, title: 'Glossary', count: count.glossary, pageKey: 'glossary' },
      ],
    },
    {
      label: 'Tools & Admin',
      cards: toolsCards,
    },
  ];

  return (
    <Container>
      <ContentContainer>
        <TileHeaderButton
          title={`Welcome back, ${localStorage.getItem('username') || 'User'}`}
          subtitle="Clinical Trial Portal Manager"
          size="lg"
          hideBreadcrumb
          onClick={() => setOpenExportModal(true)}
          buttonText={exportStatus === 'polling' ? 'Generating...' : 'Generate Report'}
          disabled={exportStatus === 'polling'}
        />
        <ConfirmModal
          title="Generate Report"
          content="Do you want to generate the full export? This may take awhile. Navigating away from this page will stop the report generation."
          open={openExportModal}
          onClose={() => setOpenExportModal(false)}
          onConfirm={handleExport}
        />
        <PageContent>
          {sections.map(s => ({ ...s, cards: s.cards.filter(c => !c.pageKey || !disabledPages.includes(c.pageKey)) })).filter(s => s.cards.length > 0).map(section => (
            <SectionWrapper key={section.label}>
              <SectionLabel>{section.label}</SectionLabel>
              <CardGrid>
                {section.cards.map(card => {
                  const Icon = card.icon;
                  return (
                    <Link to={card.to} className="card-link" key={card.to}>
                      <Card>
                        <CardHeader>
                          <IconBox>
                            {Icon && <Icon size={22} />}
                          </IconBox>
                          {card.count != null ? (
                            <CountNumber>{card.count}</CountNumber>
                          ) : (
                            <ArrowCircle>
                              <ArrowUpRight size={16} />
                            </ArrowCircle>
                          )}
                        </CardHeader>
                        <CardTitle>{card.title}</CardTitle>
                      </Card>
                    </Link>
                  );
                })}
              </CardGrid>
            </SectionWrapper>
          ))}
        </PageContent>
      </ContentContainer>
    </Container>
  );
}

export default Dashboard;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const SectionWrapper = styled.div`
  margin-bottom: 28px;
`

const SectionLabel = styled.div`
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: #9aa3b0;
  margin-bottom: 13px;
`

const CardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const Card = styled.div`
  background-color: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 14px;
  padding: 19px 19px 20px;
  cursor: pointer;
  transition: transform .16s, box-shadow .16s, border-color .16s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 26px -16px rgba(20,163,174,.55);
    border-color: #c7ecef;
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 15px;
`

const IconBox = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: #e7f6f7;
  color: #0f8994;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CountNumber = styled.div`
  font-size: 25px;
  font-weight: 800;
  color: #1f2733;
  line-height: 1;
  letter-spacing: -0.5px;
`

const ArrowCircle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f2f4f8;
  color: #9aa3b0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CardTitle = styled.div`
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  font-size: 15.5px;
  font-weight: 700;
  color: #1f2733;
  margin-bottom: 4px;
`

